import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { formatInTz } from '@/utils/timezone';
import type { CsrCohortLeadRow } from '@/types/reports';

interface JunkLeadsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Cohort rows with class = 'excluded' */
  rows: CsrCohortLeadRow[];
  timezone: string;
}

function safeFormatDate(s: string, timezone: string) {
  try {
    return formatInTz(s, timezone, 'dd MMM yyyy HH:mm');
  } catch {
    return s;
  }
}

/** QA view of leads the classifier excluded from CSR grading. */
export function JunkLeadsSheet({ open, onOpenChange, rows, timezone }: JunkLeadsSheetProps) {
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-xl">Excluded Leads — QA Review</SheetTitle>
          <SheetDescription>
            Not CSR-attributable: wrong number, spam, existing customer, out of scope, out of area,
            sales calls, parts or shop enquiries, duplicates and test leads. Excluded from Book %,
            Held % and Lost. Spot-check that the reasons were chosen honestly.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          <Badge variant="outline" className="text-sm">
            Total excluded: <span className="ml-1 font-semibold">{rows.length}</span>
          </Badge>
        </div>

        <div className="mt-6">
          {rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No excluded leads in this period.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lead #</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>CSR</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.lead_id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => {
                        onOpenChange(false);
                        navigate(`/leads/${row.lead_id}`);
                      }}
                    >
                      <TableCell className="font-mono text-xs">{row.lead_number || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[280px] truncate" title={row.lost_reason || ''}>
                        {row.lost_reason || (row.status === 'spam' ? 'Status: spam' : '-')}
                      </TableCell>
                      <TableCell className="text-sm">{row.csr_name || (row.csr_id ? 'Unknown' : 'Unattributed')}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{safeFormatDate(row.created_at, timezone)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
